import React, { useState } from 'react';
import Head from 'next/head';
import { Box, Grid } from '@mui/material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';

import {
  TabsV2,
  TaskDetailScrollableSection,
  FAUserIcon,
  FACalendar,
  FAEuroIcon,
  OrderForm,
  OrderUserDetails,
  ShortName,
  MarkAsPaidModal,
} from '@components';
import { UploadDocumentV3 } from 'components/common/media';
import { DrawerLayout, TaskLayout } from '@layouts';

import { ENTITIES_PERMISIONS } from 'components/common/PermissionsGate/constants';
import { showError } from '@utils';
import { ordersServices } from '@services';

import { TaskSection } from 'components/common/TaskSection';

import { useOrderOperations, usePermissions } from '@hooks';
import { ButtonV2 } from 'components/common/ui';

export const tabs = [
  {
    label: 'Order',
    id: 'order',
    icon: <FACalendar />,
  },
  {
    label: 'Payment & Billing',
    id: 'payment-billing',
    icon: <FAEuroIcon />,
  },
];

export const rightTabs = [
  { icon: <FAUserIcon />, id: 'user-info', label: '' },
  {
    icon: (
      <InsertDriveFileIcon fontSize="small" data-testid="InsertDriveFileIcon" />
    ),
    id: 'files',
    label: 'Files',
  },
];

const Order = () => {
  const { refresh, transactions, order, orderId, loading } =
    useOrderOperations(true);
  const { findEntity } = usePermissions();
  const [isSubmitting, setIsSubmitting] = useState();

  const handleGenerateReceipt = async () => {
    try {
      setIsSubmitting(true);
      await ordersServices.generateReceipt(orderId);
      await refresh();
      setIsSubmitting(false);
    } catch (error) {
      showError(error);
    }
  };
  const handleMarkAsPaid = async (values = {}) => {
    try {
      const payload = {
        ...values,
        paymentMethod: Number(values.paymentMethod),
        orderId: order?.id,
        isRecurring: false,
        status: 0,
      };
      await ordersServices.markOrderAsPaid(payload);
      await refresh();
    } catch (error) {
      showError(error);
    }
  };

  const renderHeaderOptions = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          gap: '8px',
          alignItems: 'center',
        }}
      >
        <ButtonV2
          size="small"
          disabled={
            loading ||
            isSubmitting ||
            !findEntity([ENTITIES_PERMISIONS.WriteOrders])
          }
          variant="outlined"
          onClick={handleGenerateReceipt}
        >
          Generate Receipt
        </ButtonV2>
        <MarkAsPaidModal order={order} onSubmit={handleMarkAsPaid} />
      </Box>
    );
  };

  const handleRefundTransaction = async ({ transactionId, amount }) => {
    try {
      await ordersServices.refundTransaction(
        {
          orderId,
          trnsId: transactionId,
        },
        {
          amount,
        }
      );
      await refresh();
    } catch (error) {
      showError(error);
    }
  };

  return (
    <>
      <Head>
        <title>{`Order ID: ${orderId} `}</title>
        <meta name="description" content="Instacar ledger" />
        <link rel="icon" href="/favicon.ico" />
      </Head>
      <TaskLayout
        task={orderId}
        onReload={refresh}
        entity={ENTITIES_PERMISIONS.ReadOrders}
        renderHeaderOptions={renderHeaderOptions}
        isLoading={loading}
      >
        <Grid container alignItems="start" spacing={1}>
          <Grid size={{ xs: 12, md: 8 }}>
            <TabsV2 tabs={tabs} observerRootId="order-details" />

            <TaskDetailScrollableSection id="order-details">
              <OrderForm
                defaultValues={{ ...order, transactions }}
                onRefund={handleRefundTransaction}
              />
            </TaskDetailScrollableSection>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TabsV2 tabs={rightTabs} observerRootId="order-detail-right" />
            <TaskDetailScrollableSection id="order-detail-right">
              <TaskSection
                isSmall
                title=""
                icon={
                  <ShortName
                    styles={{
                      width: '54px',
                      height: '54px',
                      fontSize: '1.25rem',
                    }}
                  >
                    {order?.billingInfo?.shortName}
                  </ShortName>
                }
                id={rightTabs[0].id}
                canExpand={false}
              >
                <OrderUserDetails
                  data={{ ...order?.billingInfo, userId: order?.userId }}
                />
              </TaskSection>

              <TaskSection
                isSmall
                title={rightTabs[1].label}
                id={rightTabs[1].id}
                icon={rightTabs[1].icon}
              >
                <UploadDocumentV3
                  documents={order?.documents}
                  loading={loading || isSubmitting}
                  enableUpload={false}
                />
              </TaskSection>
            </TaskDetailScrollableSection>
          </Grid>
        </Grid>
      </TaskLayout>
    </>
  );
};

Order.getLayout = (page) => {
  return <DrawerLayout>{page}</DrawerLayout>;
};

export default Order;
